import { Controller } from "@hotwired/stimulus"

// Savant Terminal command bar (docs/plans/2026-09-23-savant-terminal.md).
// Codes map to URLs the server hands over (codes value); numbers pick a menu
// option or a table row. Picking a row copies its <template> into the detail
// pane, so looking at a deal costs no server trip.
export default class extends Controller {
  static targets = ["input", "status", "clock", "option", "row", "detail"]
  static values = { codes: Object, suggestion: String }

  connect() {
    this.onKey = this.globalKey.bind(this)
    document.addEventListener("keydown", this.onKey)
    this.tick()
    this.timer = setInterval(() => this.tick(), 1000)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKey)
    clearInterval(this.timer)
  }

  run(event) {
    event.preventDefault()
    const raw = this.inputTarget.value.trim() || this.suggestionValue
    this.inputTarget.value = ""
    if (!raw) return

    if (/^\d+$/.test(raw)) return this.choose(Number(raw))

    const url = this.codesValue[raw.toUpperCase()]
    if (url) return Turbo.visit(url)

    this.say(`No function matches "${raw}". Try PIPE, TASK, EVT, INV or DESK.`)
  }

  // Keys inside the command input.
  key(event) {
    if (event.key === "Tab" && this.suggestionValue) {
      event.preventDefault()
      this.inputTarget.value = this.suggestionValue
    } else if (event.key === "Escape") {
      event.preventDefault()
      if (this.inputTarget.value) this.inputTarget.value = ""
      else history.back()
    }
  }

  // Typing anywhere lands in the command bar, unless the person is in another
  // field or on a control that uses the key itself.
  globalKey(event) {
    const el = document.activeElement
    if (el === this.inputTarget || event.metaKey || event.ctrlKey || event.altKey) return
    if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT|BUTTON|A)$/.test(el.tagName))) return
    if (event.key === "Escape") return history.back()
    if (event.key.length === 1 && event.key !== " ") this.inputTarget.focus()
  }

  pick(event) {
    if (event.target.closest("a, button")) return
    this.select(Number(event.currentTarget.dataset.index))
  }

  choose(number) {
    if (this.hasRowTarget) return this.select(number - 1)

    const option = this.optionTargets[number - 1]
    if (option) return Turbo.visit(option.href)
    this.say(`There is no option ${number} here.`)
  }

  select(index) {
    const row = this.rowTargets[index]
    if (!row || !this.hasDetailTarget) return this.say(`There is no row ${index + 1} here.`)

    this.rowTargets.forEach((r) => r.classList.toggle("sel", r === row))
    const template = row.querySelector("template[data-detail]")
    this.detailTarget.replaceChildren(template.content.cloneNode(true))
    this.say(`Row ${index + 1} selected.`)
  }

  say(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  tick() {
    if (!this.hasClockTarget) return
    this.clockTarget.textContent = new Date().toLocaleTimeString("en-US", {
      timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
    }) + " ET"
  }
}
